import { useEffect, useRef, useState } from 'react';

const DURATION_MS = 900;

/**
 * Counts a stat from 0 to `target` the first time it scrolls into view.
 *
 * Fires once — a number that re-animates every time it re-enters the viewport
 * reads as a glitch, not as polish. Honours `prefers-reduced-motion` by
 * jumping straight to the final value.
 *
 * `delayMs` holds the count back after the element appears. The hero stats are
 * on screen from the first frame but stay invisible until their pop-in, and a
 * number that finished counting behind an opacity of 0 has counted for nobody.
 */
export const useCountUp = (
  target: number,
  delayMs = 0,
): [React.RefObject<HTMLSpanElement>, number] => {
  const ref = useRef<HTMLSpanElement>(null);
  const [value, setValue] = useState(0);
  const startedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduced =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduced || typeof IntersectionObserver === 'undefined') {
      setValue(target);
      return;
    }

    let frame = 0;
    let timer = 0;

    const run = () => {
      const startedAt = performance.now();

      const step = (now: number) => {
        const progress = Math.min((now - startedAt) / DURATION_MS, 1);
        // ease-out cubic: fast off the line, settles gently on the final digit
        const eased = 1 - Math.pow(1 - progress, 3);
        setValue(Math.round(target * eased));
        if (progress < 1) frame = requestAnimationFrame(step);
      };

      frame = requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || startedRef.current) return;
          startedRef.current = true;
          observer.disconnect();
          if (delayMs > 0) timer = window.setTimeout(run, delayMs);
          else run();
        });
      },
      { threshold: 0.4 },
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [target, delayMs]);

  return [ref, value];
};
